/**
 * chatHistoryStorage.ts
 * 客服对话历史记录的 localStorage 读写工具
 *
 * 存储结构：
 *   key: `cs_chat_history_{courierId}`
 *   value: LocalHistory（JSON）
 *
 * 策略：
 *   - 最多保留 MAX_SESSIONS 条已结束会话
 *   - 每条会话最多保留 MAX_MESSAGES_PER_SESSION 条消息
 *   - 超过 DEFAULT_EXPIRY_DAYS 天（默认1天）未更新的会话自动清理
 *   - isStreaming=true 的占位消息不持久化
 */

const STORAGE_KEY_PREFIX = 'cs_chat_history_'
const MAX_SESSIONS = 100
const MAX_MESSAGES_PER_SESSION = 100
export const DEFAULT_EXPIRY_DAYS = 1 // 默认保留天数，可在调用处覆盖
const CURRENT_VERSION = 1

// ─── 类型定义 ────────────────────────────────────────────────────────────────

export interface PersistedMessage {
  id: string
  type: string
  content: string
  sender: 'user' | 'agent'
  timestamp: number
  options?: string[]
  actions?: Array<{ text: string; actionType: string; url?: string }>
  orders?: any[]
  hasMoreOption?: boolean
  isOldSession?: boolean
  tag?: number
  vmiImage?: string // AI 回复中的图片 URL（来自 content_answer.vmi_image）
  equipmentItems?: any[] // 装备订单列表（tag=50）
  equipmentTitle?: string // 装备选择时的标题
  equipmentTicket?: {
    aftersaleType: string
    orderNo: string
    equipmentName: string
    processProgress: string
    showType?: any
    showTypeDesc?: string
    reasonType?: number
  } // 装备售后进度（tag=49/51）
  processTag?: number // 退换货状态（tag=44）：1退货 / 2换货 / 3补发
  afterSalesOrderNo?: string // 装备工单号（tag=44）
  reasonType?: number // 售后原因类型（tag=45 上传物流用，quality_reason 映射后）
}

export interface SessionRecord {
  sessionId: string
  conversationId: string
  startTime: number
  endTime?: number
  messages: PersistedMessage[]
  progressNums?: number // 退换货进度数量（会话级别隔离）
}

interface LocalHistory {
  courierId: string
  version: number
  sessions: SessionRecord[]
  lastUpdated: number
}

// ─── 内部工具 ─────────────────────────────────────────────────────────────────

function getKey (courierId: string): string {
  return `${STORAGE_KEY_PREFIX}${courierId}`
}
// chatHistoryStorage.ts 新增
export function reopenSession (courierId: string, sessionId: string): void {
  const history = readHistory(courierId)
  const session = history.sessions.find(s => s.sessionId === sessionId)
  if (!session) return
  delete session.endTime // 清除 endTime，让 session 重新变为"进行中"
  writeHistory(history)
}
function createEmptyHistory (courierId: string): LocalHistory {
  return {
    courierId,
    version: CURRENT_VERSION,
    sessions: [],
    lastUpdated: Date.now()
  }
}

function readHistory (courierId: string): LocalHistory {
  try {
    const raw = localStorage.getItem(getKey(courierId))
    if (!raw) return createEmptyHistory(courierId)
    const parsed: LocalHistory = JSON.parse(raw)
    // 版本不一致时丢弃旧数据，避免格式错误
    if (parsed.version !== CURRENT_VERSION) return createEmptyHistory(courierId)
    return parsed
  } catch (e) {
    return createEmptyHistory(courierId)
  }
}

function writeHistory (history: LocalHistory): void {
  try {
    history.lastUpdated = Date.now()
    localStorage.setItem(getKey(history.courierId), JSON.stringify(history))
  } catch (e) {
    // localStorage 写满时静默失败，不影响主流程
    console.warn('[chatHistoryStorage] write failed:', e)
  }
}

// ─── 公开 API ─────────────────────────────────────────────────────────────────

/**
 * 清理过期会话，在 init 时调用一次
 * @param expiryDays 保留天数，不传则使用 DEFAULT_EXPIRY_DAYS（默认1天）
 */
export function cleanExpired (courierId: string, expiryDays: number = DEFAULT_EXPIRY_DAYS): void {
  const history = readHistory(courierId)
  const cutoff = Date.now() - expiryDays * 24 * 60 * 60 * 1000
  history.sessions = history.sessions.filter(s => (s.endTime || s.startTime) > cutoff)
  writeHistory(history)
}

/**
 * 创建一条新会话记录，返回 sessionId
 * 超过 MAX_SESSIONS 时自动淘汰最旧的一条
 */
export function createSession (courierId: string): string {
  const history = readHistory(courierId)
  const sessionId = `session_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
  const newSession: SessionRecord = {
    sessionId,
    conversationId: '',
    startTime: Date.now(),
    messages: []
  }
  if (history.sessions.length >= MAX_SESSIONS) {
    history.sessions = history.sessions.slice(-(MAX_SESSIONS - 1))
  }
  history.sessions.push(newSession)
  writeHistory(history)
  return sessionId
}

/**
 * 持久化一条消息（新增或更新）
 * isStreaming=true 的占位消息请勿传入此方法
 */
export function saveMessage (courierId: string, sessionId: string, msg: PersistedMessage): void {
  const history = readHistory(courierId)
  const session = history.sessions.find(s => s.sessionId === sessionId)
  if (!session) {
    console.warn('[saveMessage] 未找到session:', { courierId, sessionId, msgId: msg.id, existingSessions: history.sessions.map(s => s.sessionId) })
    return
  }

  const existingIndex = session.messages.findIndex(m => m.id === msg.id)
  if (existingIndex >= 0) {
    // 更新已有消息（如 streaming 完成后的最终内容）
    session.messages[existingIndex] = msg
  } else {
    // 超出上限时从头部截断旧消息
    if (session.messages.length >= MAX_MESSAGES_PER_SESSION) {
      session.messages = session.messages.slice(-(MAX_MESSAGES_PER_SESSION - 1))
    }
    session.messages.push(msg)
  }
  writeHistory(history)
}

/**
 * 将后端返回的 conversationId 同步到对应会话记录
 */
export function updateConversationId (
  courierId: string,
  sessionId: string,
  conversationId: string
): void {
  const history = readHistory(courierId)
  const session = history.sessions.find(s => s.sessionId === sessionId)
  if (!session) return
  session.conversationId = conversationId
  writeHistory(history)
}

/**
 * 恢复异常退出（进程被杀）时未写入 endTime 的会话
 * 在 init 时、createSession 之前调用一次
 * 有消息但没有 endTime 的 session 视为异常退出，补全 endTime 使其可被加载
 */
export function recoverUnfinishedSessions (courierId: string): void {
  const history = readHistory(courierId)
  let changed = false
  history.sessions.forEach(s => {
    if (!s.endTime && s.messages.length > 0) {
      const lastMsg = s.messages[s.messages.length - 1]
      s.endTime = lastMsg ? lastMsg.timestamp : s.startTime
      changed = true
    }
  })
  if (changed) writeHistory(history)
}

/**
 * 更新会话的退换货进度数量
 */
export function updateProgressNums (courierId: string, sessionId: string, nums: number): void {
  const history = readHistory(courierId)
  const session = history.sessions.find(s => s.sessionId === sessionId)
  if (!session) return
  session.progressNums = nums
  writeHistory(history)
}

/**
 * 按 sessionId 读取会话的退换货进度数量
 */
export function getProgressNums (courierId: string, sessionId: string): number | undefined {
  const history = readHistory(courierId)
  const session = history.sessions.find(s => s.sessionId === sessionId)
  return session?.progressNums
}

/**
 * 标记会话结束（写入 endTime）
 */
export function endSession (courierId: string, sessionId: string): void {
  const history = readHistory(courierId)
  const session = history.sessions.find(s => s.sessionId === sessionId)
  if (!session) return
  session.endTime = Date.now()
  writeHistory(history)
}

/**
 * 按倒序索引读取已结束的会话（0 = 最近一条，1 = 再上一条，…）
 * 返回 null 表示没有更多历史
 */
export function loadSessionByIndex (courierId: string, index: number): SessionRecord | null {
  const history = readHistory(courierId)
  // 只取已结束（有 endTime）的会话，从新到旧排列
  const completed = history.sessions
    .filter(s => s.endTime)
    .sort((a, b) => b.startTime - a.startTime)
  return completed[index] !== undefined ? completed[index] : null
}

/**
 * 已结束会话的总数（用于判断是否还有更多历史可加载）
 */
export function getCompletedSessionCount (courierId: string): number {
  return readHistory(courierId).sessions.filter(s => s.endTime).length
}
